import type { Thunk, Action, Computed } from 'easy-peasy';
import { action, thunk, computed } from 'easy-peasy';
import type { Injections } from '../../services/injections';
import type { ApplicationErrorOptions } from '../../../types';
import type { FetchAssetParams, FulfillmentContextLinkProperty } from '../types';
import { AssetWorkflow } from '../../workflow/AssetWorkflow';
import parseStatusCode from '../../errors/parseStatusCode';
import type { AssetTypes } from '../../workflow/types';

export interface FulfillmentContextLinkPropertiesModel {
  fetchFulfillmentContextLinkProperties: Thunk<
    FulfillmentContextLinkPropertiesModel,
    FetchAssetParams,
    Injections,
    Promise<FulfillmentContextLinkProperty[]>
  >;
  fetchFulfillmentContextLinkProperty: Thunk<
    FulfillmentContextLinkPropertiesModel,
    string,
    Injections,
    Promise<FulfillmentContextLinkProperty>
  >;
  fulfillmentContextLinkPropertiesActionStart: Action<FulfillmentContextLinkPropertiesModel, void>;
  lastAction?: number;
  fulfillmentContextLinkProperties: Record<string, FulfillmentContextLinkProperty>;
  fulfillmentContextLinkFailure: Action<FulfillmentContextLinkPropertiesModel, ApplicationErrorOptions>;
  setFulfillmentContextLinks: Action<FulfillmentContextLinkPropertiesModel, FulfillmentContextLinkProperty[]>;
  getFulfillmentContextLinkById: Computed<
    FulfillmentContextLinkPropertiesModel,
    (assetId: string, assetType: string) => FulfillmentContextLinkProperty[]
  >;
}

export const fulfillmentContextLinkModel: FulfillmentContextLinkPropertiesModel = {
  fulfillmentContextLinkProperties: {},
  fulfillmentContextLinkFailure: action((state, payload) => {
    console.error(payload);
  }),
  setFulfillmentContextLinks: action((state, payload) => {
    for (const curr of payload) {
      state.fulfillmentContextLinkProperties[curr.fulfillmentContextLinkPropertyId] = curr;
    }
  }),
  fulfillmentContextLinkPropertiesActionStart: action((state, payload) => {
    state.lastAction = Date.now();
  }),
  fetchFulfillmentContextLinkProperties: thunk(async (actions, payload, { injections }) => {
    try {
      actions.fulfillmentContextLinkPropertiesActionStart();
      const res = await injections.assetService.fetchFulfillmentContextLinkProperties(payload);
      actions.setFulfillmentContextLinks(res);
      return res;
    } catch (error) {
      actions.fulfillmentContextLinkFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured fetching fulfillment context links',
        asNotification: true,
      });
    }
  }),
  fetchFulfillmentContextLinkProperty: thunk(async (actions, payload, { injections }) => {
    try {
      actions.fulfillmentContextLinkPropertiesActionStart();
      const res = await injections.assetService.fetchFulfillmentContextLinkProperty(payload);
      actions.setFulfillmentContextLinks([res]);
      return res;
    } catch (error) {
      actions.fulfillmentContextLinkFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured fetching fulfillment context',
        asNotification: true,
      });
    }
  }),
  getFulfillmentContextLinkById: computed((state) => (assetId: string, assetType: string) => {
    const assetProperty = AssetWorkflow.getAssetIdKeyForAssetType(assetType as AssetTypes);
    return Object.values(state.fulfillmentContextLinkProperties).filter(
      (linkProperty: any) => linkProperty?.[assetProperty] === assetId,
    );
  }),
};
